import React, { useState } from 'react'
import moment from 'moment'
import { useHistory } from 'react-router'
import { ButtonBase } from '@mui/material'
import { colors } from '@resiliatech/birdsong'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useMutation } from '@apollo/client'
import { EDIT_GOAL_GROUP } from 'Apollo/Mutations/EditGoalGroup'
import { GOAL_GROUP_CARDS } from 'Apollo/Queries/GoalGroupCards'
import {
  CreateGoalGroupInput,
  GoalGroupCard as GoalGroupCardType,
} from 'Generated/graphql-types'
import { useAppState } from 'Context/AppContext/AppContext'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import { categoriesIncludingOld } from 'TypeDefs/Goal'
import { GoalProgress } from 'Components/Pages/Impact/GoalsDashboard/Goals/GoalProgress'
import { analytics } from 'Modules/Analytics'
import {
  FUNDER_IMPACT_GOAL_GROUP_PATH,
  GOAL_DATE_FORMAT,
} from 'Components/Pages/Impact/GoalPage/utils'
import { funderGoals } from 'Modules/Analytics/trackActionInterface/FunderPlatform/funderGoals'
import {
  Category,
  Date as DateLabel,
  Details,
  GoalCardContainer,
  DateInfo,
  GoalDescription,
  GoalTitle,
  Dates,
  TaskStatus,
  TopSection,
  CategoryAndStatus,
  EditOptions,
  FunderGranteeCount,
  GranteeTotal,
  TargetProgress,
  ChipsWrapper,
  LeftChipsWrapper,
  PeopleIcon,
  TinyDot,
  CategoryContainer,
} from './FunderGoals.styled'
import { GoalGroupModal } from './GoalGroupModal/GoalGroupModal'

type GoalGroupCardProps = {
  goalGroup: GoalGroupCardType
  isInteractive?: boolean
}

const iconStyles = {
  color: colors.interactive.primaryEnabled,
  cursor: 'pointer ',
  fontSize: 'large',
  marginRight: '10px',
}

export const GoalGroupCard = ({
  goalGroup,
  isInteractive = goalGroup.numGrantees > 0,
}: GoalGroupCardProps) => {
  const {
    userConfig: { id, email },
    funderOrgConfig: { orgId: funderOrgId },
  } = useAppState()
  const { popToast } = useSnackbar()
  const history = useHistory()
  const [showEditGoalModal, setShowEditGoalModal] = useState(false)

  const [editGoalGroup, { loading: editGoalGroupLoading }] = useMutation(
    EDIT_GOAL_GROUP,
    {
      refetchQueries: [GOAL_GROUP_CARDS],
    }
  )

  const goalCategory =
    goalGroup.category && categoriesIncludingOld[goalGroup.category]
      ? categoriesIncludingOld[goalGroup.category]
      : ''

  const dispatchGoalCardClicked = () => {
    analytics.trackActionCreator(funderGoals.Funder_Collab_Goal_Clicked)({
      goalId: goalGroup.goalGroupId,
      userId: id,
      userEmail: email,
      funderOrgId,
    })
  }

  const dispatchEditGoalClicked = () => {
    analytics.trackActionCreator(
      funderGoals.Funder_Collab_Goal_Edit_Goal_Clicked
    )({
      goalId: goalGroup.goalGroupId,
    })
  }

  const handleRouteToGoal = () => {
    history.push(`${FUNDER_IMPACT_GOAL_GROUP_PATH}/${goalGroup.goalGroupId}`)
    dispatchGoalCardClicked()
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowEditGoalModal(true)
    dispatchEditGoalClicked()
  }

  const handleEditGoalGroup = async (
    updatedGoalGroup: CreateGoalGroupInput
  ) => {
    try {
      await editGoalGroup({
        variables: {
          funderOrgId,
          goalGroupId: goalGroup.goalGroupId,
          data: updatedGoalGroup,
        },
      })
      setShowEditGoalModal(false)
      popToast({ content: 'Goal successfully updated' })
      return true
    } catch {
      popToast({ content: 'Failed to update goal' })
      return false
    }
  }

  const numTasksIncomplete = goalGroup.totalTasks - goalGroup.completedTasks
  const numTasksTotal = goalGroup.totalTasks
  const numTasksComplete = numTasksTotal - numTasksIncomplete
  const taskStatusPercentage = Math.round(
    (numTasksComplete / numTasksTotal) * 100
  )
  const taskStatusIndicator =
    taskStatusPercentage === 100 ? (
      <CheckCircleOutlineOutlinedIcon />
    ) : (
      <GoalProgress value={taskStatusPercentage} showLabel={false} size={20} />
    )
  const hasTasks = numTasksTotal > 0
  const hasTarget = goalGroup.target && goalGroup.target > 0
  const hasGrantees = goalGroup.numGrantees > 0
  const numGrantees = goalGroup.numGrantees || 0

  const targetProgressStatusPercentage = goalGroup.target
    ? Math.round((goalGroup.targetProgress / goalGroup.target) * 100)
    : 0
  const targetProgressStatusIndicator =
    targetProgressStatusPercentage === 100 ? (
      <CheckCircleOutlineOutlinedIcon />
    ) : (
      <GoalProgress
        value={targetProgressStatusPercentage}
        showLabel={false}
        size={20}
      />
    )

  const BaseContainer = isInteractive ? ButtonBase : React.Fragment

  const getVariant = (taskStatusPercent: number) => {
    if (taskStatusPercent === 0) return 'warning'
    if (taskStatusPercent === 100) return 'completed'
    return 'informational'
  }

  return (
    <>
      <BaseContainer>
        <GoalCardContainer
          onClick={isInteractive ? handleRouteToGoal : undefined}
          isInteractive={isInteractive}
        >
          <Details>
            <TopSection>
              <CategoryAndStatus>
                {goalCategory && goalCategory !== 'None' && (
                  <CategoryContainer>
                    <Category>{goalCategory}</Category>
                    <TinyDot color={colors.feedback.infoDark} />
                    <Category>
                      {goalGroup.isFunderAssigned
                        ? 'Funder-Assigned'
                        : 'Collaborated Goal'}
                    </Category>
                  </CategoryContainer>
                )}
                <EditOptions>
                  <EditOutlinedIcon onClick={handleEditClick} sx={iconStyles} />
                </EditOptions>
              </CategoryAndStatus>
              {goalGroup.title && (
                <GoalTitle data-testid='goalTitle'>{goalGroup.title}</GoalTitle>
              )}
              <Dates>
                <DateInfo>
                  <DateLabel variant='caption'>
                    Start Date:{' '}
                    {goalGroup.startDate
                      ? moment.utc(goalGroup.startDate).format(GOAL_DATE_FORMAT)
                      : 'Not Specified'}
                  </DateLabel>
                  <TinyDot />
                  <DateLabel variant='caption'>
                    Due Date:{' '}
                    {goalGroup.endDate
                      ? moment.utc(goalGroup.endDate).format(GOAL_DATE_FORMAT)
                      : 'Not Specified'}
                  </DateLabel>
                </DateInfo>
              </Dates>
            </TopSection>
            {goalGroup.description && (
              <GoalDescription variant='body1'>
                {goalGroup.description}
              </GoalDescription>
            )}
            <ChipsWrapper>
              <LeftChipsWrapper>
                {hasTarget && (
                  <TargetProgress
                    variant={getVariant(targetProgressStatusPercentage)}
                  >
                    {targetProgressStatusPercentage > 0 &&
                      targetProgressStatusIndicator}
                    {targetProgressStatusPercentage}% to Goal Target
                  </TargetProgress>
                )}
                {hasTasks && (
                  <TaskStatus variant={getVariant(taskStatusPercentage)}>
                    {taskStatusPercentage > 0 && taskStatusIndicator}
                    {taskStatusPercentage}% Tasks Completed
                  </TaskStatus>
                )}
              </LeftChipsWrapper>
              {hasGrantees && (
                <GranteeTotal variant='granteeList'>
                  <PeopleIcon />
                  <FunderGranteeCount>
                    {numGrantees} Grantee(s)
                  </FunderGranteeCount>
                </GranteeTotal>
              )}
            </ChipsWrapper>
          </Details>
        </GoalCardContainer>
      </BaseContainer>
      {showEditGoalModal && (
        <GoalGroupModal
          loading={editGoalGroupLoading}
          isOpen={showEditGoalModal}
          onClose={() => setShowEditGoalModal(false)}
          goalGroup={goalGroup}
          onSubmit={handleEditGoalGroup}
        />
      )}
    </>
  )
}
